import { useUserRestrictions } from '@hooks/useUserRestrictions';
import {
    HomeIcon,
    SettingsIcon,
    TabBarContainer,
    CircleButtonContainer,
    GradientCircle,
    BackgroundContainer,
    TabContentArea,
    TabItem,
    TabLabel,
    InvisibleSpacer
} from './TabBar.styles';
import { useTranslation } from 'react-i18next';

const TabBar = ({ navigate }: { navigate: (path: string) => void }) => {
    const { t, i18n } = useTranslation();
    const { canPay } = useUserRestrictions();
    const canPayEnabled = canPay();
    return (
        <TabBarContainer>
            {/* Circle button positioned to hover at the top */}
            {canPayEnabled && <CircleButtonContainer
                onClick={() => navigate('/bank-transfer-payment')}
            >
                <GradientCircle style={{ fontSize: i18n.language === 'ru' ? '12px' : '16px' }}>
                    {t('components.tabBar.payButton')}
                </GradientCircle>
            </CircleButtonContainer>}

            {/* Background with gradient - positioned behind */}
            <BackgroundContainer>
                <svg width="100%" height="100%" preserveAspectRatio="xMidYMin slice" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        <linearGradient id="blueVioletGradient" x1="0" y1="0" x2="13.8454" y2="197.279" gradientUnits="userSpaceOnUse">
                            <stop offset="0.012144" stopColor="#C9ECFF" />
                            <stop offset="0.334211" stopColor="#82ADFF" />
                            <stop offset="0.62186" stopColor="#7B69FF" />
                            <stop offset="0.953221" stopColor="#9B1BFF" />
                        </linearGradient>

                        <linearGradient id="overlayGradient" x1="50%" y1="-132.911" x2="50%" y2="102.089" gradientUnits="userSpaceOnUse">
                            <stop offset="0.139218" stopColor="white" stopOpacity="0" />
                            <stop offset="1" stopColor="white" />
                        </linearGradient>

                        {/* 
                    CUTOUT CIRCLE CONFIGURATION
                    - Circle positioned at the top center
                */}
                        <mask id="circleMask">
                            <rect width="100%" height="100%" fill="white" />
                            {canPayEnabled && <circle cx="50%" cy="20px" r="42px" fill="black" />}
                        </mask>

                        <filter id="innerShadow" x="0" y="0" width="100%" height="100%" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                            <feOffset dy="1" />
                            <feGaussianBlur stdDeviation="0.5" />
                            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
                            <feColorMatrix type="matrix" values="0 0 0 0 0.745833 0 0 0 0 0.727188 0 0 0 0 0.727188 0 0 0 0.25 0" />
                            <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
                        </filter>
                    </defs>

                    <g filter="url(#innerShadow)">
                        {/* Base rectangle with gradient and mask */}
                        <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="url(#blueVioletGradient)"
                            mask="url(#circleMask)"
                        />

                        {/* White overlay at 60% opacity */}
                        <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="white"
                            fillOpacity="0.6"
                            mask="url(#circleMask)"
                        />

                        {/* Vertical gradient overlay at 80% opacity */}
                        <rect
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            fill="url(#overlayGradient)"
                            fillOpacity="0.8"
                            mask="url(#circleMask)"
                        />
                    </g>
                </svg>
            </BackgroundContainer>

            {/* Tab bar content area */}
            <TabContentArea>
                {/* Tab items */}
                <TabItem onClick={() => navigate('/home')}>
                    <div style={{ width: 32, height: 32 }}>
                        <HomeIcon />
                    </div>
                    <TabLabel>
                        {t('components.tabBar.homeTab')}
                    </TabLabel>
                </TabItem>
                <InvisibleSpacer>{t('components.tabBar.centerLabel')}</InvisibleSpacer> {/* Invisible spacer for the center button */}
                <TabItem onClick={() => navigate('/settings')}>
                    <div style={{ width: 32, height: 32 }}>
                        <SettingsIcon />
                    </div>
                    <TabLabel>
                        {t('components.tabBar.settingsTab')}
                    </TabLabel>
                </TabItem>
            </TabContentArea>
        </TabBarContainer>
    );
}

export default TabBar
